import React from 'react';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { FormAreaSelect } from 'ui/components/elements/FormAreaSelect/FormAreaSelect';
import { FormSelect } from 'ui/components/elements/FormSelect/FormSelect';
import { FormMultiCheckBox } from 'ui/components/elements/FormMultiCheckBox/FormMultiCheckBox';
import { FormError } from 'ui/components/elements/FormError/FormError';
import { FormLabel } from 'ui/components/elements/FormLabel/FormLabel';
import { FormInput } from 'ui/components/elements/FormInput/FormInput';
import { ActionButton } from 'ui/components/elements/ActionButton/ActionButton';
import { SnackBar } from 'ui/components/elements/SnackBar/SnackBar';
import { useBuyerProfileRegistration } from './useBuyerProfileRegistration';
import { mqMin } from 'utils/responsive';

const BgWrapper = styled.div`
    background-color: var(--light-orange);
    height: 100%;
    min-height: 100svh;
    padding: 40px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const Wrapper = styled.div`
    margin: 0 auto;
    padding: 40px 30px;
    border-radius: 12px;
    background-color: var(--white);
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    font-size: var(--font-medium);
    text-align: center;

    ${mqMin('NARROW')} {
        width: 600px;
    }
`;

const Title = styled.div`
    font-size: var(--font-x-large);
    color: var(--dark-blue);
    font-weight: 600;
    margin-bottom: 5px;
    text-align: center;
`;

const ButtonContainer = styled.div`
    margin: 20px auto 0;
`;

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const RangeContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    margin: 0 auto;
`;

const RangeSelectsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TildeText = styled.span`
    font-size: var(--font-medium);
    color: var(--dark-blue);
    font-weight: 600;
    margin: 0 5px;
`;

const RangeSelectWrapper = styled.div`
    flex: 1;
`;

export const BuyerProfileRegistration: React.FC = () => {
    const {
        control,
        loading,
        errors,
        areaChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        showSnackbar,
        industryChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        hasAcquisitionExperienceChoices,
        setShowSnackbar,
        submit,
    } = useBuyerProfileRegistration();

    return (
        <BgWrapper>
            <Wrapper>
                <SnackBar message="企業プロフィールを設定しました。" open={showSnackbar} setOpen={setShowSnackbar} />
                <Title>ご希望の買収条件についてご入力ください</Title>
                <span>※ご希望条件・プロフィールはご登録後に編集可能です</span>
                <FlexContainer>
                    <FormLabel title="買収希望案件エリア" />
                    {areaChoices && areaChoices.length > 0 && (
                        <Controller
                            name="target_area"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormAreaSelect {...field} options={areaChoices} />
                                    {errors.target_area && <FormError message={errors.target_area.message} />}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <FlexContainer>
                    <FormLabel title="買収希望案件業種" />
                    {industryChoices && industryChoices.length > 0 && (
                        <Controller
                            name="target_company_industries"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <FormMultiCheckBox {...field} options={industryChoices} idPrefix="industry" />
                                    {errors.target_company_industries && (
                                        <FormError message={errors.target_company_industries.message} />
                                    )}
                                </div>
                            )}
                        />
                    )}
                </FlexContainer>
                <RangeContainer>
                    <FormLabel title="買収希望金額" />
                    <RangeSelectsContainer>
                        {purchaseAmountLowerChoices && purchaseAmountLowerChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_purchase_amount_lower"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={purchaseAmountLowerChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="下限を選択してください"
                                            />
                                            {errors.target_company_purchase_amount_lower && (
                                                <FormError
                                                    message={errors.target_company_purchase_amount_lower.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                        <TildeText>〜</TildeText>
                        {purchaseAmountUpperChoices && purchaseAmountUpperChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_purchase_amount_upper"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={purchaseAmountUpperChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="上限を選択してください"
                                            />
                                            {errors.target_company_purchase_amount_upper && (
                                                <FormError
                                                    message={errors.target_company_purchase_amount_upper.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>
                <RangeContainer>
                    <FormLabel title="買収希望案件企業の従業員数" />
                    <RangeSelectsContainer>
                        {employeeLowerChoices && employeeLowerChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_employee_size_lower"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={employeeLowerChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="下限を選択してください"
                                            />
                                            {errors.target_company_employee_size_lower && (
                                                <FormError
                                                    message={errors.target_company_employee_size_lower.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                        <TildeText>〜</TildeText>
                        {employeeUpperChoices && employeeUpperChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_employee_size_upper"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={employeeUpperChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="上限を選択してください"
                                            />
                                            {errors.target_company_employee_size_upper && (
                                                <FormError
                                                    message={errors.target_company_employee_size_upper.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>

                <RangeContainer>
                    <FormLabel title="買収希望案件の売上高" />
                    <RangeSelectsContainer>
                        {revenueScaleLowerChoices && revenueScaleLowerChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_revenue_scale_lower"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={revenueScaleLowerChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="下限を選択してください"
                                            />
                                            {errors.target_company_revenue_scale_lower && (
                                                <FormError
                                                    message={errors.target_company_revenue_scale_lower.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                        <TildeText>〜</TildeText>
                        {revenueScaleUpperChoices && revenueScaleUpperChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_revenue_scale_upper"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={revenueScaleUpperChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="上限を選択してください"
                                            />
                                            {errors.target_company_revenue_scale_upper && (
                                                <FormError
                                                    message={errors.target_company_revenue_scale_upper.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>
                <RangeContainer>
                    <FormLabel title="買収希望案件の営業利益" />
                    <RangeSelectsContainer>
                        {profitLevelLowerChoices && profitLevelLowerChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_profit_level_lower"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={profitLevelLowerChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="下限を選択してください"
                                            />
                                            {errors.target_company_profit_level_lower && (
                                                <FormError message={errors.target_company_profit_level_lower.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                        <TildeText>〜</TildeText>
                        {profitLevelUpperChoices && profitLevelUpperChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_profit_level_upper"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={profitLevelUpperChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="上限を選択してください"
                                            />
                                            {errors.target_company_profit_level_upper && (
                                                <FormError message={errors.target_company_profit_level_upper.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>
                <RangeContainer>
                    <FormLabel title="買収希望案件企業の純資産金額" />
                    <RangeSelectsContainer>
                        {netAssetsLevelLowerChoices && netAssetsLevelLowerChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_net_assets_level_lower"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={netAssetsLevelLowerChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="下限を選択してください"
                                            />
                                            {errors.target_company_net_assets_level_lower && (
                                                <FormError
                                                    message={errors.target_company_net_assets_level_lower.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                        <TildeText>〜</TildeText>
                        {netAssetsLevelUpperChoices && netAssetsLevelUpperChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="target_company_net_assets_level_upper"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={netAssetsLevelUpperChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="上限を選択してください"
                                            />
                                            {errors.target_company_net_assets_level_upper && (
                                                <FormError
                                                    message={errors.target_company_net_assets_level_upper.message}
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>
                <RangeContainer>
                    <FormLabel title="過去の買収実績の有無" />
                    <RangeSelectsContainer>
                        {hasAcquisitionExperienceChoices && hasAcquisitionExperienceChoices.length > 0 && (
                            <RangeSelectWrapper>
                                <Controller
                                    name="has_acquisition_experience"
                                    control={control}
                                    render={({ field }) => (
                                        <div>
                                            <FormSelect
                                                {...field}
                                                value={field.value ?? ''}
                                                options={hasAcquisitionExperienceChoices.map(([value, label]) => ({
                                                    value: value,
                                                    label: label,
                                                }))}
                                                placeholder="選択してください"
                                            />
                                            {errors.has_acquisition_experience && (
                                                <FormError message={errors.has_acquisition_experience.message} />
                                            )}
                                        </div>
                                    )}
                                />
                            </RangeSelectWrapper>
                        )}
                    </RangeSelectsContainer>
                </RangeContainer>
                <FlexContainer>
                    <FormLabel title="招待コード" />
                    <Controller
                        name="invitation_code"
                        control={control}
                        render={({ field }) => (
                            <div>
                                <FormInput {...field} placeholder="お持ちの方のみ入力してください" />
                                {errors.invitation_code && <FormError message={errors.invitation_code.message} />}
                            </div>
                        )}
                    />
                </FlexContainer>

                <ButtonContainer>
                    <ActionButton
                        disabled={loading}
                        onClick={submit}
                        bgColor="main-color-blue"
                        style={{ minWidth: '200px', maxWidth: '400px' }}
                    >
                        登録する
                    </ActionButton>
                </ButtonContainer>
            </Wrapper>
        </BgWrapper>
    );
};
